import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import i18n from "@/i18n";
import { supabase } from "@/lib/supabase";
import { useSoftwareProducts, type SoftwareProduct } from "@/pages/products/hooks/useSoftwareProducts";
import {
  isDelisted,
  recommendSameCategory,
  useProductCatalogStatus,
  type CatalogStatusEntry,
} from "@/pages/products/hooks/useProductCatalogStatus";

export interface ApplyFormValues {
  productSlug: string;
  productVersion: string;
  edition: string;
  seatCount: string;
  machineCode: string;
  applicantName: string;
  email: string;
  company: string;
  useCase: string;
}

export type FieldErrors = Partial<Record<keyof ApplyFormValues, string>>;

export interface SubmittedApplication {
  reference: string;
  productName: string;
  productVersion: string;
  edition: string;
  seatCount: number;
  applicantName: string;
  email: string;
  company: string;
  machineCode: string;
  submittedAt: string;
}

const EMPTY_VALUES: ApplyFormValues = {
  productSlug: "",
  productVersion: "",
  edition: "standard",
  seatCount: "1",
  machineCode: "",
  applicantName: "",
  email: "",
  company: "",
  useCase: "",
};

export function useApplyForm() {
  const { products, loading: productsLoading, error: productsError, reload } = useSoftwareProducts();
  const catalogStatus = useProductCatalogStatus();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState<ApplyFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<SubmittedApplication | null>(null);
  const presetApplied = useRef<string | null>(null);

  const selectedProduct: SoftwareProduct | null = useMemo(
    () => products.find((product) => product.slug === values.productSlug) ?? null,
    [products, values.productSlug],
  );

  // 下架兜底：若当前选中的产品链接指向已停止授权（下架 / 草稿）的产品，给出提示并阻止提交。
  const archivedProduct: CatalogStatusEntry | null = useMemo(() => {
    if (!values.productSlug || selectedProduct) return null;
    const entry = catalogStatus.bySlug[values.productSlug];
    if (entry && isDelisted(entry.status)) return entry;
    return null;
  }, [values.productSlug, selectedProduct, catalogStatus.bySlug]);

  // 应用链接参数：首次进入或链接指向新的产品时自动载入；
  // 指向已下架产品时也会记录下来，以便走「已停止授权」兜底提示。
  useEffect(() => {
    if (products.length === 0) return;
    const slug = searchParams.get("product");
    if (!slug || presetApplied.current === slug) return;
    presetApplied.current = slug;
    const product = products.find((item) => item.slug === slug);
    if (product) {
      setValues((prev) => ({
        ...prev,
        productSlug: slug,
        productVersion: product.latest_version ?? "",
        edition: "standard",
      }));
    } else {
      setValues((prev) => ({ ...prev, productSlug: slug }));
    }
  }, [products, searchParams]);

  // 同为已下架产品推荐在售替代品（同分类）。
  const recommendedProducts: CatalogStatusEntry[] = useMemo(() => {
    if (!archivedProduct) return [];
    return recommendSameCategory(catalogStatus.entries, archivedProduct.category, [archivedProduct.slug]);
  }, [archivedProduct, catalogStatus.entries]);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const user = data.session?.user;
      if (!user) return;
      setValues((prev) => ({
        ...prev,
        applicantName: prev.applicantName || ((user.user_metadata?.full_name as string | undefined) ?? ""),
        email: prev.email || (user.email ?? ""),
      }));
    });
    return () => {
      active = false;
    };
  }, []);

  const updateField = useCallback(
    (key: keyof ApplyFormValues, value: string) => {
      setValues((prev) => {
        if (key === "productSlug") {
          const product = products.find((item) => item.slug === value);
          return { ...prev, productSlug: value, productVersion: product?.latest_version ?? "", edition: "standard" };
        }
        return { ...prev, [key]: value };
      });
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    },
    [products],
  );

  const validate = useCallback((): FieldErrors => {
    const next: FieldErrors = {};
    if (!values.productSlug) next.productSlug = i18n.t("apply.errors.product");
    if (!values.productVersion) next.productVersion = i18n.t("apply.errors.version");
    if (!values.edition) next.edition = i18n.t("apply.errors.edition");
    const seats = Number(values.seatCount);
    if (!values.seatCount || Number.isNaN(seats) || seats < 1) next.seatCount = i18n.t("apply.errors.seats");
    if (!values.machineCode.trim()) next.machineCode = i18n.t("apply.errors.machine");
    if (!values.applicantName.trim()) next.applicantName = i18n.t("apply.errors.name");
    if (!values.email.trim()) next.email = i18n.t("apply.errors.email");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = i18n.t("apply.errors.emailFormat");
    if (values.useCase.length > 500) next.useCase = i18n.t("apply.errors.useCase");
    return next;
  }, [values]);

  const submit = useCallback(async (): Promise<boolean> => {
    if (archivedProduct) {
      setSubmitError(i18n.t("apply.errors.archived"));
      return false;
    }
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !selectedProduct) return false;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id ?? null;
      const seatCount = Number(values.seatCount);

      const payload = {
        user_id: userId,
        applicant_name: values.applicantName.trim(),
        email: values.email.trim(),
        company: values.company.trim() || null,
        product_name: selectedProduct.name,
        product_version: values.productVersion,
        edition: values.edition,
        seat_count: seatCount,
        machine_code: values.machineCode.trim(),
        use_case: values.useCase.trim() || null,
        status: "pending",
      };

      const { data, error } = await supabase
        .from("license_applications")
        .insert(payload)
        .select("id")
        .maybeSingle();
      if (error) throw error;

      const record = data as { id: number } | null;
      setSubmitted({
        reference: record?.id ? `APP-${String(record.id).padStart(6, "0")}` : `APP-${Date.now().toString().slice(-8)}`,
        productName: selectedProduct.name,
        productVersion: values.productVersion,
        edition: values.edition,
        seatCount,
        applicantName: payload.applicant_name,
        email: payload.email,
        company: payload.company ?? "",
        machineCode: payload.machine_code,
        submittedAt: new Date().toISOString(),
      });
      return true;
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : i18n.t("apply.errors.submitFailed"));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [validate, selectedProduct, values, archivedProduct]);

  const reset = useCallback(() => {
    setValues(EMPTY_VALUES);
    setErrors({});
    setSubmitError(null);
    setSubmitted(null);
  }, []);

  return {
    products,
    productsLoading,
    productsError,
    reloadProducts: reload,
    values,
    errors,
    updateField,
    submitting,
    submitError,
    submitted,
    submit,
    reset,
    selectedProduct,
    archivedProduct,
    recommendedProducts,
  };
}